import React from 'react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { TeamInformationSharesLineChartProps } from './types'
import { TeamInformationSharesLineChartTooltip } from './TeamInformationSharesLineChartTooltip'
import { TeamInformationSharesLineChartLegend } from './TeamInformationSharesLineChartLegend'
import { CHART_CONFIG, AXIS_COLOR, TEAM_COLORS } from './constants'
import { TeamIdentifier } from './types'
import { INTERAX_COLORS } from '../../../../../../constants/colors'

/**
 * Team Information Shares Line Chart Component
 *
 * Displays a line chart showing team performance data over periods
 */
export const TeamInformationSharesLineChart: React.FC<TeamInformationSharesLineChartProps> = ({
  data,
  height = 400,
  width = '100%'
}) => {
  // Check if any data point has team1Shares to determine if we should render Team 1 line
  const hasTeam1Data = data.some((point) => point.team1Shares !== undefined && point.team1Shares !== null)
  // Check if any data point has team2Shares to determine if we should render Team 2 line
  const hasTeam2Data = data.some((point) => point.team2Shares !== undefined && point.team2Shares !== null)

  return (
    <ResponsiveContainer width={width} height={height}>
      <LineChart data={data} margin={CHART_CONFIG.margin}>
        {/* Dotted grid lines */}
        <CartesianGrid stroke={AXIS_COLOR} vertical={false} strokeDasharray="3 3" opacity={0.5} />

        {/* XAxis for the sequential 'period' number */}
        <XAxis
          dataKey="period"
          stroke={AXIS_COLOR}
          tickLine={false}
          axisLine={false}
          tick={{ fill: AXIS_COLOR, fontSize: 12 }}
          padding={{ left: 10, right: 10 }}
        />

        {/* YAxis for the 'shares' count */}
        <YAxis
          stroke={AXIS_COLOR}
          tickLine={false}
          axisLine={false}
          domain={CHART_CONFIG.yAxisDomain}
          ticks={CHART_CONFIG.yAxisTicks}
          tick={{ fill: AXIS_COLOR, fontSize: 12 }}
          tickFormatter={(value) => value.toString()}
          width={CHART_CONFIG.yAxisWidth}
        />

        {/* Custom Tooltip */}
        <Tooltip content={<TeamInformationSharesLineChartTooltip />} />

        {/* Legend using custom component */}
        <Legend
          verticalAlign="top"
          align="left"
          content={<TeamInformationSharesLineChartLegend />}
          wrapperStyle={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            padding: 0
          }}
        />

        {/* Line for Team 1 (Orange) - only render when team1Shares data exists */}
        {hasTeam1Data && (
          <Line
            type="natural"
            dataKey="team1Shares"
            name={TeamIdentifier.TEAM_1}
            stroke={TEAM_COLORS.TEAM_1}
            strokeWidth={CHART_CONFIG.lineStrokeWidth}
            dot={false}
            activeDot={{
              r: CHART_CONFIG.activeDotRadius,
              stroke: TEAM_COLORS.TEAM_1,
              fill: INTERAX_COLORS.white,
              strokeWidth: CHART_CONFIG.activeDotStrokeWidth
            }}
          />
        )}

        {/* Line for Team 2 (Grey) - only render when team2Shares data exists */}
        {hasTeam2Data && (
          <Line
            type="natural"
            dataKey="team2Shares"
            name={TeamIdentifier.TEAM_2}
            stroke={TEAM_COLORS.TEAM_2}
            strokeWidth={CHART_CONFIG.lineStrokeWidth}
            dot={false}
            activeDot={{
              r: CHART_CONFIG.activeDotRadius,
              stroke: TEAM_COLORS.TEAM_2,
              fill: INTERAX_COLORS.white,
              strokeWidth: CHART_CONFIG.activeDotStrokeWidth
            }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  )
}
